//import { AnnotationStatus } from '@admiin-com/ds-graphql';
import { Task, TaskDirection, TaskGuest } from '@admiin-com/ds-graphql';
import type { AnnotationsUnion, Color } from '@nutrient-sdk/viewer';
import { renderToString } from 'react-dom/server';
import { v4 as uuidv4 } from 'uuid';

export const dataURLToBlob = (dataURL: string) => {
  const parts = dataURL.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const uInt8Array = new Uint8Array(raw.length);

  for (let i = 0; i < raw.length; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
};
async function getPageVisibleRect(pdfSignatureRef: any, pageIndex: any) {
  const NutrientViewer: any = await import('@nutrient-sdk/viewer');
  // Page DOM element.
  const pageEl = pdfSignatureRef.contentDocument.querySelector(
    `.PSPDFKit-Page[data-page-index="${pageIndex}"]`
  );
  const pageBoundingClientRect = pageEl.getBoundingClientRect();
  // Viewport DOM element.
  const viewportEl =
    pdfSignatureRef.contentDocument.querySelector('.PSPDFKit-Viewport');

  // Get the visible page area in page units.
  return pdfSignatureRef.transformContentClientToPageSpace(
    new NutrientViewer.Geometry.Rect({
      left: Math.max(pageBoundingClientRect.left, 0),
      top: Math.max(pageBoundingClientRect.top, 0),
      width: Math.min(pageEl.clientWidth, viewportEl.clientWidth),
      height: Math.min(pageEl.clientHeight, viewportEl.clientHeight),
    }),
    pageIndex
  );
}

export async function isAnnotationInsidePage(
  pdfSignatureRef: any,
  pageIndex: any,
  annotation: any
) {
  const pageRect = await getPageVisibleRect(pdfSignatureRef, pageIndex);
  return annotation.boundingBox.isRectOverlapping(pageRect);
}
// remove blank space from canvas
export function trimCanvas(canvas: HTMLCanvasElement) {
  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');
  if (!croppedCtx) {
    return '';
  }
  croppedCanvas.width = canvas.width;
  croppedCanvas.height = canvas.height;
  croppedCtx.drawImage(canvas, 0, 0);

  let w = croppedCanvas.width;
  let h = croppedCanvas.height;
  const pix: {
    x: number[];
    y: number[];
  } = { x: [], y: [] };
  const imageData = croppedCtx.getImageData(0, 0, w, h);

  let index = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      index = (y * w + x) * 4;

      if (
        imageData.data[index] === 255 &&
        imageData.data[index + 1] === 255 &&
        imageData.data[index + 2] === 255 &&
        imageData.data[index + 3] === 255
      ) {
        continue;
      }

      if (imageData.data[index + 3] > 0) {
        pix.x.push(x);
        pix.y.push(y);
      }
    }
  }

  pix.x.sort((a, b) => a - b);
  pix.y.sort((a, b) => a - b);
  const n = pix.x.length - 1;

  if (pix.x.length === 0 && pix.y.length === 0) {
    return '';
  }
  w = pix.x[n] - pix.x[0];
  h = pix.y[n] - pix.y[0];
  const cut = croppedCtx.getImageData(pix.x[0], pix.y[0], w, h);

  croppedCanvas.width = w + 40;
  croppedCanvas.height = h + 40;
  croppedCtx.putImageData(cut, 20, 20);

  return croppedCanvas.toDataURL();
}

export const getExtensionFromContentType = (contentType: string) =>
  contentType.split('/').pop();

export function getBlobFromCanvas(canvas: HTMLCanvasElement) {
  const dataUrl: string = trimCanvas(canvas);
  return dataURLToBlob(dataUrl);
}

export async function downloadPdf(
  pdfSignatureRef: any,
  taskGuest?: TaskGuest | Task | null
) {
  const pdf = await pdfSignatureRef?.current?.exportPDF({
    flatten: true,
  });
  const blob = new Blob([pdf], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = taskGuest?.reference ?? 'document.pdf';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const createCustomSignatureNode = (
  placeholderUi: any,
  customData: any
) => {
  const isPlaceholderView = customData?.status === 'PENDING'; //AnnotationStatus.PENDING;
  const div = document.createElement('div');
  if (isPlaceholderView) {
    div.style.cssText = 'height: 100%;display: flex;align-items: center;';
    //@ts-ignore TODO: resolve type issue
    div.innerHTML = renderToString(placeholderUi);
  }
  return div;
};

const getDefaultAnnotationData = async (annotation: AnnotationsUnion) => {
  const NutrientViewer: any = await import('@nutrient-sdk/viewer');
  const { pageIndex, boundingBox, name, id, customData } = annotation;
  const bbox = (annotation as any).bbox;
  let rectData;

  if (boundingBox) {
    // boundingBox object format
    rectData = {
      left: boundingBox.left,
      top: boundingBox.top + 8,
      width: boundingBox.width,
      height: boundingBox.height,
    };
  } else if (bbox && Array.isArray(bbox)) {
    // bbox array format [x, y, width, height]
    rectData = {
      left: bbox[0],
      top: bbox[1] + 8,
      width: bbox[2],
      height: bbox[3],
    };
  } else {
    throw new Error('Annotation boundingBox or bbox is required');
  }

  const updatedId = uuidv4();
  const annotationObj = {
    pageIndex,
    boundingBox: new NutrientViewer.Geometry.Rect({
      left: rectData.left,
      top: rectData.top,
      width: rectData.width,
      height: rectData.height,
    }),
    id: updatedId,
    lockedContents: true,
    fontSize: 12,
    customData: {
      ...customData,
      //status: AnnotationStatus.ACTIONED,
      status: 'ACTIONED',
    },
    horizontalAlign: 'left' as const,
    verticalAlign: 'bottom' as const,
    backgroundColor: NutrientViewer.Color.TRANSPARENT,
  };
  return annotationObj;
};

export const getAnnotation = async (
  annotation: AnnotationsUnion,
  signatureAttachmentId: string,
  readOnly = false
) => {
  const NutrientViewer: any = await import('@nutrient-sdk/viewer');
  const defaultdAnnotation = await getDefaultAnnotationData(annotation);
  let updateAnnotation;
  if (annotation?.customData?.type === 'DATE') {
    updateAnnotation = new NutrientViewer.Annotations.TextAnnotation({
      ...defaultdAnnotation,
      readOnly,
      text: {
        format: 'plain',
        value: new Date().toLocaleDateString() || '2024',
      },
    });
  } else if (annotation?.customData?.type === 'SIGNATURE') {
    updateAnnotation = new NutrientViewer.Annotations.ImageAnnotation({
      ...defaultdAnnotation,
      readOnly,
      contentType: 'image/jpeg',
      imageAttachmentId: signatureAttachmentId,
    });
  } else if (annotation?.customData?.type === 'NAME') {
    updateAnnotation = new NutrientViewer.Annotations.TextAnnotation({
      ...defaultdAnnotation,
      readOnly,
      text: {
        format: 'plain',
        value: (annotation?.customData?.name as string) || 'Sample text', // TODO : added text
      },
    });
  }
  return updateAnnotation;
};
export function checkIfValidHexColor(hex: string): boolean {
  return /^#[0-9A-F]{6}$/i.test(hex);
}
const SIGNATURE_COLORS = [
  { iconColour: '#E5CA4C', backgroundColor: '#F7EDC4' },
  { iconColour: '#9DDC8B', backgroundColor: '#D7F7CE' },
  { iconColour: '#8FCFE7', backgroundColor: '#C4E9F7' },
  { iconColour: '#CEA3FC', backgroundColor: '#DDC4F7' },
  { iconColour: '#FFB48F', backgroundColor: '#F7CEBA' },
];

export function getSignatureColor(index: number) {
  if (index === -1) return '#FFFFFF';
  return SIGNATURE_COLORS[index % 5].backgroundColor;
}

export function getSignatureIconColor(backgroundColor: string) {
  const index = SIGNATURE_COLORS.findIndex(
    (color) => color.backgroundColor === backgroundColor
  );
  return SIGNATURE_COLORS[index % 5]?.iconColour ?? '#fff';
}

export const checkAnnotationDirection = (annotationsJson: string) => {
  if (!annotationsJson) return TaskDirection.SENDING;

  const parsedData = JSON.parse(annotationsJson);
  const annotations = parsedData?.annotations;

  if (!Array.isArray(annotations)) {
    return TaskDirection.SENDING;
  }

  for (const annotation of annotations) {
    if (
      annotation.customData?.status === 'PENDING' &&
      annotation.customData?.signerType !== 'ENTITY_USER'
    )
      return TaskDirection.SENDING;
  }
  return TaskDirection.RECEIVING;
};

export const signedAndPendingUserNames = (
  annotationsJson: string | null | undefined
) => {
  if (!annotationsJson) return { signedUsersName: [], pendingUsersName: [] };

  const annotations = JSON.parse(annotationsJson)?.annotations;
  if (!Array.isArray(annotations)) {
    return { signedUsersName: [], pendingUsersName: [] };
  }

  const pendingUsersNameSet = new Set<string>();
  const signedUsersNameSet = new Set<string>();
  for (const annotation of annotations) {
    if (annotation.customData?.status === 'ACTIONED') {
      signedUsersNameSet.add(annotation.customData?.name as string);
    } else {
      pendingUsersNameSet.add(annotation.customData?.name as string);
    }
  }

  const pendingUsersName = Array.from(pendingUsersNameSet);
  const signedUsersName = Array.from(signedUsersNameSet);
  return { signedUsersName, pendingUsersName };
};

export const isAllSigned = (annotationsJson: string | null | undefined) => {
  if (!annotationsJson) return false;
  const annotations = JSON.parse(annotationsJson)?.annotations;
  if (!Array.isArray(annotations)) return false;
  return annotations.every(
    (annotation: any) => annotation.customData?.status === 'ACTIONED'
  );
};

export const getSignersFromAnnotations = (
  task: Task | string | null | undefined
) => {
  if (!task) return [];
  const annotationsJson = typeof task === 'string' ? task : task.annotations;
  if (!annotationsJson) return [];
  const annotations = JSON.parse(annotationsJson)?.annotations;
  if (!annotations) return [];
  // Use a Map to keep track of unique signers by ID
  const signersMap = new Map();

  annotations
    .filter((annotation: any) => annotation.customData?.type === 'SIGNATURE')
    .forEach((annotation: any) => {
      const signerId = annotation.customData?.signerId;
      if (signerId && !signersMap.has(signerId)) {
        signersMap.set(signerId, {
          id: signerId,
          ...annotation.customData,
          email: annotation.customData?.email,
          name: annotation.customData?.name,
          signerType: annotation.customData?.signerType,
          entityId:
            annotation.customData?.entityId ??
            (typeof task === 'string' ? null : task.entityId),
          color: annotation.customData?.color,
          signatureKey: annotation.imageAttachmentId,
          mobile: annotation.customData?.mobile,
        });
      }
    });

  // Determine if each signer had "initial pages" checked by looking for
  // auto-added INITIAL annotations associated with that signer
  signersMap.forEach((signer, signerId) => {
    const hasAutoAddedInitials = annotations.some(
      (annotation: any) =>
        annotation.customData?.type === 'INITIAL' &&
        annotation.customData?.signerId === signerId &&
        annotation.customData?.autoAdded
    );
    if (hasAutoAddedInitials) {
      signersMap.set(signerId, { ...signer, initialPages: true });
    }
  });

  return Array.from(signersMap.values());
};

export const uploadPdfToSignedUrl = async (pdf: any, signedUrl: string) => {
  const response = await fetch(signedUrl, {
    method: 'PUT',
    body: pdf,
    headers: {
      'Content-Type': pdf.mimeType,
    },
  });

  await new Promise((resolve) => setTimeout(resolve, 4000));

  if (!response.ok) {
    throw new Error('Failed to upload PDF to the signed URL');
  }
};
export const getPageCountFromPdf = async (url: string) => {
  const { getNutrientConfig } = await import(
    '../features/hooks/useNutrientPreload/useNutrientPreload'
  );
  const NutrientViewer: any = await import('@nutrient-sdk/viewer');
  const instance = await NutrientViewer.load({
    document: url,
    ...getNutrientConfig(),
    toolbarItems: [],
    disableTextSelection: false,
    headless: true,
    styleSheets: ['/viewer.css'],
    // instantJSON: annotations ? JSON.parse(annotations) : null,
  });
  // Renders the first page (page index 0).
  return await instance.totalPageCount;
  // instanceRef.current = instance;
};

export const getPendingSignersFromAnnotations = (annotationsJson: string) => {
  const signers = getSignersFromAnnotations(annotationsJson);
  return signers.filter((signer: any) => signer.status === 'PENDING');
};

export const getPendingNonEntityUserSignersFromAnnotations = (
  annotationsJson: string
) => {
  const signers = getSignersFromAnnotations(annotationsJson);
  return signers.filter(
    (signer: any) =>
      signer.status === 'PENDING' && signer.signerType !== 'ENTITY_USER'
  );
};
